import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectModal = ({ project, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setActiveTab('overview');
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !project) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Eye' },
    { id: 'technical', label: 'Technical Details', icon: 'Code2' },
    { id: 'process', label: 'Development Process', icon: 'GitBranch' },
    { id: 'results', label: 'Results & Impact', icon: 'TrendingUp' }
  ];

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setIsImageModalOpen(true);
  };

  const handleDemoClick = () => {
    if (project?.demoUrl) {
      window.open(project?.demoUrl, '_blank');
    }
  };

  const handleGithubClick = () => {
    if (project?.githubUrl) {
      window.open(project?.githubUrl, '_blank');
    }
  };

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Modal Content */}
        <div className="relative w-full max-w-6xl max-h-[90vh] bg-background rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-md border-b border-border p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h2 className="text-2xl font-bold text-primary">{project?.title}</h2>
                  {project?.status && (
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      project?.status === 'Live' ?'bg-success text-success-foreground' 
                        : project?.status === 'In Progress' ?'bg-warning text-warning-foreground' :'bg-muted text-muted-foreground'
                    }`}>
                      {project?.status}
                    </span>
                  )}
                </div>
                <p className="text-text-secondary mb-4">{project?.description}</p>
                
                {/* Action Buttons */}
                <div className="flex space-x-3">
                  {project?.demoUrl && (
                    <Button
                      variant="default"
                      size="sm"
                      iconName="ExternalLink"
                      iconPosition="left"
                      iconSize={16}
                      onClick={handleDemoClick}
                    >
                      Live Demo
                    </Button>
                  )}
                  {project?.githubUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Github"
                      iconPosition="left"
                      iconSize={16}
                      onClick={handleGithubClick}
                    >
                      View Code
                    </Button>
                  )}
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                iconSize={20}
                onClick={onClose}
                className="ml-4"
              />
            </div>

            {/* Tabs */}
            <div className="flex space-x-1 mt-6 bg-muted p-1 rounded-lg">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    activeTab === tab?.id
                      ? 'bg-background text-primary shadow-soft'
                      : 'text-muted-foreground hover:text-text-secondary'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span className="hidden sm:inline">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-200px)] p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-8">
                {/* Project Image */}
                <div className="relative">
                  <Image
                    src={project?.image}
                    alt={project?.title}
                    className="w-full h-64 object-cover rounded-xl cursor-pointer hover:opacity-90 transition-opacity duration-300"
                    onClick={() => handleImageClick({ src: project?.image, alt: project?.title })}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl pointer-events-none" />
                </div>

                {/* Project Info Grid */}
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Challenge */}
                    <div>
                      <h3 className="text-lg font-semibold text-primary mb-3 flex items-center space-x-2">
                        <Icon name="Target" size={20} className="text-accent" />
                        <span>Challenge</span>
                      </h3>
                      <p className="text-text-secondary leading-relaxed">{project?.challenge}</p>
                    </div>

                    {/* Solution */}
                    <div>
                      <h3 className="text-lg font-semibold text-primary mb-3 flex items-center space-x-2">
                        <Icon name="Lightbulb" size={20} className="text-accent" />
                        <span>Solution</span>
                      </h3>
                      <p className="text-text-secondary leading-relaxed">{project?.solution}</p>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Project Details */}
                    <div>
                      <h3 className="text-lg font-semibold text-primary mb-3">Project Details</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">Type</span>
                          <span className="text-text-secondary font-medium">{project?.type}</span>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">Industry</span>
                          <span className="text-text-secondary font-medium">{project?.industry}</span>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">Year</span>
                          <span className="text-text-secondary font-medium">{project?.year}</span>
                        </div>
                        <div className="flex items-center justify-between py-2">
                          <span className="text-muted-foreground">Duration</span>
                          <span className="text-text-secondary font-medium">{project?.duration}</span>
                        </div>
                      </div>
                    </div>

                    {/* Key Metrics */}
                    {project?.metrics && (
                      <div>
                        <h3 className="text-lg font-semibold text-primary mb-3">Key Metrics</h3>
                        <div className="grid grid-cols-2 gap-4">
                          {project?.metrics?.map((metric, index) => (
                            <div key={index} className="text-center p-4 bg-muted rounded-lg">
                              <div className="text-2xl font-bold text-accent mb-1">{metric?.value}</div>
                              <div className="text-sm text-muted-foreground">{metric?.label}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Technology Stack */}
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-4 flex items-center space-x-2">
                    <Icon name="Layers" size={20} className="text-accent" />
                    <span>Technology Stack</span>
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {project?.technologies?.map((tech, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 bg-accent/10 text-accent font-medium rounded-lg border border-accent/20"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Technical Details Tab */}
            {activeTab === 'technical' && (
              <div className="space-y-8">
                {/* Architecture */}
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-4 flex items-center space-x-2">
                    <Icon name="Network" size={20} className="text-accent" />
                    <span>Architecture Overview</span>
                  </h3>
                  <p className="text-text-secondary leading-relaxed mb-4">{project?.architecture}</p>
                  
                  {project?.architectureImage && (
                    <Image
                      src={project?.architectureImage}
                      alt="Architecture Diagram"
                      className="w-full h-64 object-contain bg-muted rounded-xl cursor-pointer"
                      onClick={() => handleImageClick({ src: project?.architectureImage, alt: 'Architecture Diagram' })}
                    />
                  )}
                </div>

                {/* Code Snippets */}
                {project?.codeSnippets && (
                  <div>
                    <h3 className="text-lg font-semibold text-primary mb-4 flex items-center space-x-2">
                      <Icon name="Code" size={20} className="text-accent" />
                      <span>Key Implementation</span>
                    </h3>
                    <div className="space-y-4">
                      {project?.codeSnippets?.map((snippet, index) => (
                        <div key={index} className="bg-code-bg rounded-xl overflow-hidden">
                          <div className="flex items-center justify-between px-4 py-2 bg-muted border-b border-border">
                            <span className="text-sm font-medium text-text-secondary">{snippet?.title}</span>
                            <span className="text-xs text-muted-foreground">{snippet?.language}</span>
                          </div>
                          <pre className="p-4 text-sm text-code-text font-mono overflow-x-auto code-scrollbar">
                            <code>{snippet?.code}</code>
                          </pre>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Technical Decisions */}
                {project?.technicalDecisions && (
                  <div>
                    <h3 className="text-lg font-semibold text-primary mb-4 flex items-center space-x-2">
                      <Icon name="GitBranch" size={20} className="text-accent" />
                      <span>Technical Decisions</span>
                    </h3>
                    <div className="space-y-4">
                      {project?.technicalDecisions?.map((decision, index) => (
                        <div key={index} className="p-4 bg-muted rounded-lg">
                          <h4 className="font-semibold text-primary mb-2">{decision?.title}</h4>
                          <p className="text-text-secondary text-sm leading-relaxed">{decision?.rationale}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Development Process Tab */}
            {activeTab === 'process' && (
              <div className="space-y-8">
                {/* Timeline */}
                {project?.timeline && (
                  <div>
                    <h3 className="text-lg font-semibold text-primary mb-6 flex items-center space-x-2">
                      <Icon name="Clock" size={20} className="text-accent" />
                      <span>Development Timeline</span>
                    </h3>
                    <div className="space-y-4">
                      {project?.timeline?.map((phase, index) => (
                        <div key={index} className="flex items-start space-x-4">
                          <div className="flex-shrink-0 w-8 h-8 bg-accent rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold text-accent-foreground">{index + 1}</span>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-primary mb-1">{phase?.phase}</h4>
                            <p className="text-text-secondary text-sm mb-2">{phase?.description}</p>
                            <span className="text-xs text-muted-foreground">{phase?.duration}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Challenges & Solutions */}
                {project?.challenges && (
                  <div>
                    <h3 className="text-lg font-semibold text-primary mb-4 flex items-center space-x-2">
                      <Icon name="AlertTriangle" size={20} className="text-warning" />
                      <span>Challenges & Solutions</span>
                    </h3>
                    <div className="space-y-4">
                      {project?.challenges?.map((item, index) => (
                        <div key={index} className="p-4 border border-border rounded-lg">
                          <h4 className="font-semibold text-warning mb-2 flex items-center space-x-2">
                            <Icon name="AlertCircle" size={16} />
                            <span>{item?.challenge}</span>
                          </h4>
                          <p className="text-text-secondary text-sm leading-relaxed">{item?.solution}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Results & Impact Tab */}
            {activeTab === 'results' && (
              <div className="space-y-8">
                {/* Impact Metrics */}
                {project?.impact && (
                  <div>
                    <h3 className="text-lg font-semibold text-primary mb-6 flex items-center space-x-2">
                      <Icon name="TrendingUp" size={20} className="text-success" />
                      <span>Business Impact</span>
                    </h3>
                    <div className="grid md:grid-cols-3 gap-6">
                      {project?.impact?.map((metric, index) => (
                        <div key={index} className="text-center p-6 bg-success/5 border border-success/20 rounded-xl">
                          <div className="text-3xl font-bold text-success mb-2">{metric?.value}</div>
                          <div className="text-sm font-medium text-primary mb-1">{metric?.label}</div>
                          <div className="text-xs text-muted-foreground">{metric?.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Lessons Learned */}
                {project?.lessonsLearned && (
                  <div>
                    <h3 className="text-lg font-semibold text-primary mb-4 flex items-center space-x-2">
                      <Icon name="BookOpen" size={20} className="text-accent" />
                      <span>Lessons Learned</span>
                    </h3>
                    <div className="space-y-3">
                      {project?.lessonsLearned?.map((lesson, index) => (
                        <div key={index} className="flex items-start space-x-3 p-4 bg-muted rounded-lg">
                          <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                          <p className="text-text-secondary text-sm leading-relaxed">{lesson}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Future Improvements */}
                {project?.futureImprovements && (
                  <div>
                    <h3 className="text-lg font-semibold text-primary mb-4 flex items-center space-x-2">
                      <Icon name="Zap" size={20} className="text-accent" />
                      <span>Future Improvements</span>
                    </h3>
                    <div className="space-y-3">
                      {project?.futureImprovements?.map((improvement, index) => (
                        <div key={index} className="flex items-start space-x-3 p-4 border border-border rounded-lg">
                          <Icon name="ArrowRight" size={16} className="text-accent mt-0.5 flex-shrink-0" />
                          <p className="text-text-secondary text-sm leading-relaxed">{improvement}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Image Modal */}
      {isImageModalOpen && selectedImage && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setIsImageModalOpen(false)}
          />
          <div className="relative max-w-4xl max-h-[90vh] bg-background rounded-xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-semibold text-primary">{selectedImage?.alt}</h3>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                iconSize={20}
                onClick={() => setIsImageModalOpen(false)}
              />
            </div>
            <div className="p-4">
              <Image
                src={selectedImage?.src}
                alt={selectedImage?.alt}
                className="w-full h-auto max-h-[70vh] object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectModal;